import React from 'react';
import clsx from 'clsx';
import { Download } from 'lucide-react';
import { FilterBar } from '../../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../../components/ui/SearchInput';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { Switch } from '../../../../../components/ui/Switch';
import { Button } from '../../../../../components/ui/Button';
import {
  ALL_FILTER,
  STOCK_STATE_OPTIONS,
  STOCK_VIEW,
} from '../../../../../features/stock/stockConstants';

const VIEW_OPTIONS = [
  { value: STOCK_VIEW.CONSOLIDATED, label: 'Consolidado por SKU' },
  { value: STOCK_VIEW.DETAIL, label: 'Detalle por ubicación' },
];

/**
 * Filter strip of the inventory listing.
 *
 * @param {object} props
 * @param {object} props.filters - The slice's `filters`.
 * @param {(patch: object) => void} props.onFilterChange
 * @param {string} props.searchInput - The text being typed; the page debounces
 *   it before it becomes `filters.search`.
 * @param {(value: string) => void} props.onSearchInputChange
 * @param {Array<{value: number, label: string}>} props.clientOptions - Without
 *   the "Todos" sentinel, which is prepended here. With a single client the
 *   select is not drawn: there is nothing to narrow.
 * @param {boolean} props.canSeeDetail - Whether the view switch is offered.
 * @param {() => void} props.onExport
 * @param {boolean} props.canExport
 */
export const InventoryFilters = ({
  filters,
  onFilterChange,
  searchInput,
  onSearchInputChange,
  clientOptions = [],
  canSeeDetail,
  onExport,
  canExport,
}) => (
  <FilterBar
    variant="bare"
    testId="inventory-filters"
    actions={
      <Button
        variant="ghost"
        icon={Download}
        onClick={onExport}
        disabled={!canExport}
        title={canExport ? 'Excel de las filas que estás viendo' : 'No hay filas que exportar'}
        data-testid="inventory-export"
      >
        Exportar
      </Button>
    }
  >
    {/* One row per location can be searched by the location too; a row per
        SKU cannot say which location matched, so there it stays by product. */}
    <SearchInput
      variant="bare"
      value={searchInput}
      onChange={onSearchInputChange}
      placeholder={
        filters.view === STOCK_VIEW.DETAIL
          ? 'Buscar por SKU, nombre o ubicación...'
          : 'Buscar por SKU o nombre del producto...'
      }
      testId="inventory-search"
    />

    {clientOptions.length > 1 && (
      <FilterableSelect
        label="Cliente:"
        value={filters.company}
        onChange={(company) => onFilterChange({ company })}
        options={[{ value: ALL_FILTER, label: 'Todos' }, ...clientOptions]}
        searchPlaceholder="Buscar cliente..."
        panelMinWidth={240}
        testId="inventory-client"
      />
    )}

    <FilterableSelect
      label="Estado:"
      value={filters.state}
      onChange={(state) => onFilterChange({ state })}
      options={STOCK_STATE_OPTIONS}
      testId="inventory-state"
    />

    {/* A segmented control: one white strip, and the view in use tinted
        green. The filled grey of `secondary` is the page's own background,
        so an active button in it read as loose text. */}
    {canSeeDetail && (
      <div
        role="group"
        aria-label="Vista del inventario"
        data-testid="inventory-view"
        className="inline-flex items-center gap-1 p-1 rounded-xl border border-border bg-white dark:bg-[#1a1a1a]"
      >
        {VIEW_OPTIONS.map((option) => {
          const isActive = filters.view === option.value;
          return (
            <Button
              key={option.value}
              size="sm"
              variant="subtle"
              aria-pressed={isActive}
              data-testid={`inventory-view-${option.value}`}
              onClick={() => onFilterChange({ view: option.value })}
              className={clsx(
                'rounded-lg whitespace-nowrap',
                isActive && 'bg-ninja-greenLight dark:bg-ninja-green/20 text-ninja-green hover:text-ninja-green hover:bg-ninja-greenLight dark:hover:bg-ninja-green/20'
              )}
            >
              {option.label}
            </Button>
          );
        })}
      </div>
    )}

    {/* Off by default: a combination that went to zero is noise, until the
        question is where something that used to be there went. */}
    <Switch
      checked={filters.includeZero}
      onChange={(includeZero) => onFilterChange({ includeZero })}
      label="Incluir saldos en cero"
      testId="inventory-include-zero"
    />
  </FilterBar>
);
